import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tokenPersistence } from '../utils/tokenPersistence';

interface AuthState {
  isAuthenticated: boolean;
  username: string;
  username_en: string;
  department: string;
  department_en: string;
  role?: string;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
}

const loadInitialState = (): AuthState => {
  const tokens = tokenPersistence.loadTokens();
  const hasValidTokens = tokenPersistence.hasValidTokens();
  
  return {
    isAuthenticated: hasValidTokens,
    username: tokens.userEmail || "",
    username_en: tokens.userEmail || "",
    department: "",
    department_en: "",
    role: tokens.role || "",
    accessToken: tokens.accessToken || "",
    idToken: tokens.idToken || "",
    refreshToken: tokens.refreshToken || "",
  };
};

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserFromOIDC: (state: AuthState, action: PayloadAction<{
      email: string;
      given_name?: string;
      family_name?: string;
      department?: string;
      role?: string;
      access_token: string;
      id_token?: string;
      refresh_token?: string;
    }>) => {
      state.isAuthenticated = true;
      state.username = `${action.payload.family_name || ''} ${action.payload.given_name || ''}`.trim() || action.payload.email;
      state.username_en = state.username;
      state.department = action.payload.department || '';
      state.department_en = action.payload.department || '';
      state.accessToken = action.payload.access_token;
      state.idToken = action.payload.id_token;
      state.refreshToken = action.payload.refresh_token;
      
      tokenPersistence.saveTokens({
        accessToken: action.payload.access_token,
        idToken: action.payload.id_token,
        refreshToken: action.payload.refresh_token,
        userEmail: action.payload.email,
        role: action.payload.role,
      });
    },
    setUserFromCustomAuth: (state: AuthState, action: PayloadAction<{
      email: string;
      role?: string;
      access_token: string;
      id_token?: string;
      refresh_token?: string;
    }>) => {
      state.isAuthenticated = true;
      state.username = action.payload.email;
      state.username_en = action.payload.email;
      state.department = '';
      state.department_en = '';
      state.role = action.payload.role || '';
      state.accessToken = action.payload.access_token;
      state.idToken = action.payload.id_token;
      state.refreshToken = action.payload.refresh_token;
      
      tokenPersistence.saveTokens({
        accessToken: action.payload.access_token,
        idToken: action.payload.id_token,
        refreshToken: action.payload.refresh_token,
        userEmail: action.payload.email,
        role: action.payload.role,
      });
    },
    clearUser: (state: AuthState) => {
      state.isAuthenticated = false;
      state.username = "";
      state.username_en = "";
      state.department = "";
      state.department_en = "";
      state.role = "";
      state.accessToken = undefined;
      state.idToken = undefined;
      state.refreshToken = undefined;
      
      tokenPersistence.clearTokens();
      
      sessionStorage.setItem('isAuthenticated', 'false');
      sessionStorage.removeItem('lastPath');
    },
  },
});

export const { setUserFromOIDC, setUserFromCustomAuth, clearUser } = authSlice.actions;

export default authSlice.reducer;
